/**
 * API client — axios con JWT auto-refresh.
 * - Allega access token su ogni request
 * - On 401: tenta refresh + retry; se fallisce → clear store + redirect /login
 */

import axios, { AxiosError, type AxiosRequestConfig } from "axios";
import { useAuthStore } from "./auth-store";
import { tr } from "../i18n";

const baseURL = import.meta.env.VITE_API_BASE_URL ?? "/api/v1";

export const api = axios.create({
  baseURL,
  timeout: 30000,
});

// === Request: attach access token ===
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Multi-lega: la lega scelta viaggia in un header (assente = lega di default)
  const legaId = useAuthStore.getState().legaId;
  if (legaId) {
    config.headers["X-Lega-Id"] = legaId;
  }
  return config;
});

// === Response: auto-refresh su 401 ===
let refreshPromise: Promise<string | null> | null = null;

async function attemptRefresh(): Promise<string | null> {
  const { refreshToken, setTokens, clear } = useAuthStore.getState();
  if (!refreshToken) return null;

  try {
    const res = await axios.post(
      `${baseURL}/auth/refresh`,
      { refresh_token: refreshToken },
      { timeout: 10000 },
    );
    const newAccess = res.data.access_token as string;
    // Refresh non rotated → manteniamo il refresh token esistente
    setTokens(newAccess, refreshToken);
    return newAccess;
  } catch {
    clear();
    return null;
  }
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as
      | (AxiosRequestConfig & { _retry?: boolean; _retryLega?: boolean })
      | undefined;
    // Lingua (2026-10-09): i messaggi d'errore del backend sono in italiano;
    // se en.json ha la frase, si traduce qui una volta per tutte le pagine.
    const dati = error.response?.data as { detail?: unknown } | undefined;
    const detailOriginale = dati && typeof dati.detail === "string" ? dati.detail : null;
    if (dati && detailOriginale !== null) dati.detail = tr(detailOriginale);
    // Multi-lega: la lega scelta non e' piu' accessibile → torna alla lega
    // di default e riprova una volta senza header.
    if (
      originalRequest &&
      error.response?.status === 403 &&
      !originalRequest._retryLega &&
      useAuthStore.getState().legaId &&
      detailOriginale === "Non fai parte di questa lega"
    ) {
      useAuthStore.getState().setLegaId(null);
      originalRequest._retryLega = true;
      const headers = originalRequest.headers as Record<string, unknown> | undefined;
      if (headers) delete headers["X-Lega-Id"];
      return api(originalRequest);
    }
    if (!originalRequest || error.response?.status !== 401) {
      return Promise.reject(error);
    }
    // No retry su /auth/login / /auth/refresh stessi
    if (
      originalRequest.url?.includes("/auth/login") ||
      originalRequest.url?.includes("/auth/refresh")
    ) {
      return Promise.reject(error);
    }
    if (originalRequest._retry) {
      // Già provato una volta: bail-out
      useAuthStore.getState().clear();
      return Promise.reject(error);
    }
    originalRequest._retry = true;

    // De-duplicazione: se più request si vedono 401 simultaneamente,
    // tutte aspettano lo stesso refresh
    refreshPromise ??= attemptRefresh();
    const newAccess = await refreshPromise;
    refreshPromise = null;

    if (!newAccess) {
      // Refresh fallito → user deve rifare login
      return Promise.reject(error);
    }

    // Retry con nuovo token
    originalRequest.headers = {
      ...originalRequest.headers,
      Authorization: `Bearer ${newAccess}`,
    };
    return api(originalRequest);
  },
);
